import React, { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Logo } from "./Logo";
import { BUSINESS_INFO, NAV_LINKS } from "../config";
import { useCmsCollection } from "../hooks/useCms";

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Load menu items from CMS 'sider'
  const { items: cmsPages } = useCmsCollection("sider", { sort: "sort_order" });

  const menuItems = (() => {
    if (!cmsPages || cmsPages.length === 0) {
      return NAV_LINKS;
    }
    const list = cmsPages
      .filter((page) => page.is_in_menu !== false)
      .map((page) => ({
        label: page.navigation_label || page.title,
        path: page.path || `/${page.slug?._ || page.slug || ""}`,
      }));

    // Ensure Galleri link is present in the navigation
    const hasGallery = list.some(
      (item) =>
        item.path === "/galleri" ||
        item.label?.toLowerCase().includes("galleri") ||
        item.label?.toLowerCase().includes("billede")
    );
    if (!hasGallery) {
      const omOsIdx = list.findIndex(
        (i) => i.path === "/om-os" || i.path === "/om-hundens-pels-og-pleje" || i.path === "/kontakt"
      );
      const galleryItem = { label: "Galleri", path: "/galleri" };
      if (omOsIdx !== -1) {
        list.splice(omOsIdx, 0, galleryItem);
      } else {
        list.push(galleryItem);
      }
    }
    return list;
  })();

  // Split menu items to place on each side of the logo
  const splitIndex = Math.ceil(menuItems.length / 2);
  const leftItems = menuItems.slice(0, splitIndex);
  const rightItems = menuItems.slice(splitIndex);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  const renderNavLink = (item) => (
    <NavLink
      key={item.path}
      to={item.path}
      end={item.path === "/"}
      className={({ isActive }) =>
        `relative rounded-xl px-3.5 py-2 text-sm font-medium tracking-wide transition-colors duration-150 whitespace-nowrap ${
          isActive
            ? "text-black font-semibold underline decoration-1 underline-offset-6"
            : "text-neutral-700 hover:text-black"
        }`
      }
    >
      {item.label}
    </NavLink>
  );

  return (
    <>
      {/* Solid white navbar - both initial and sticky */}
      <header className="sticky top-0 z-40 w-full border-b border-[#E8DFD5] bg-white shadow-xs">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Desktop Navigation (>= 1200px): Links on each side of the logo */}
          <div className="hidden min-[1200px]:grid min-[1200px]:grid-cols-[1fr_auto_1fr] items-center h-24 sm:h-28 py-2">
            {/* Left menu items */}
            <nav className="flex items-center justify-end gap-1 xl:gap-3 pr-6 xl:pr-8" aria-label="Venstre navigation">
              {leftItems.map(renderNavLink)}
            </nav>

            {/* Center Logo */}
            <div className="flex items-center justify-center px-4">
              <Logo size="lg" />
            </div>

            {/* Right menu items + CTA */}
            <div className="flex items-center justify-between gap-1 xl:gap-3 pl-6 xl:pl-8">
              <nav className="flex items-center gap-1 xl:gap-3" aria-label="Højre navigation">
                {rightItems.map(renderNavLink)}
              </nav>

              <a
                href={BUSINESS_INFO.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#dabeb9] px-6 py-2.5 text-xs sm:text-sm font-bold text-black shadow-xs transition-colors hover:bg-[#c9a7a0] active:scale-98 whitespace-nowrap ml-auto"
              >
                <i className="fa-solid fa-calendar-check text-black" aria-hidden="true" />
                <span className="whitespace-nowrap">Book tid</span>
              </a>
            </div>
          </div>

          {/* Mobile & Tablet Header (< 1200px) */}
          <div className="flex min-[1200px]:hidden h-20 sm:h-24 items-center justify-between py-2">
            <Logo size="md" />

            <div className="flex items-center gap-3">
              {/* Burger menu toggle: displayed ONLY under 1200px - no box behind */}
              <button
                type="button"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="flex h-11 w-11 items-center justify-center border-0 bg-transparent text-black transition-colors focus:outline-hidden p-0"
                aria-expanded={mobileMenuOpen}
                aria-label={mobileMenuOpen ? "Luk menu" : "Åbn menu"}
              >
                <i
                  className={`fa-solid ${
                    mobileMenuOpen ? "fa-xmark" : "fa-bars"
                  } text-2xl`}
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Fullscreen Mobile/Tablet Menu Drawer */}
      {mobileMenuOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Mobilmenu"
          className="min-[1200px]:hidden fixed inset-0 z-50 flex flex-col bg-white overflow-y-auto"
        >
          {/* Drawer Header */}
          <div className="flex h-20 items-center justify-between px-4 sm:px-6 border-b border-[#E8DFD5] bg-white">
            <Logo size="md" />
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="flex h-11 w-11 items-center justify-center border-0 bg-transparent text-black transition-colors p-0"
              aria-label="Luk menu"
            >
              <i className="fa-solid fa-xmark text-2xl" aria-hidden="true" />
            </button>
          </div>

          {/* Drawer Navigation Links */}
          <div className="flex-1 px-4 py-6 sm:px-6 space-y-2 bg-white">
            {menuItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === "/"}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center justify-between py-3 px-2 text-base font-semibold transition-all ${
                    isActive
                      ? "text-black font-bold"
                      : "text-neutral-700 hover:text-black"
                  }`
                }
              >
                <span>{item.label}</span>
                <i className="fa-solid fa-chevron-right text-xs opacity-60" aria-hidden="true" />
              </NavLink>
            ))}
          </div>

          {/* Drawer Footer & Contact */}
          <div className="border-t border-[#E8DFD5] bg-white p-5 sm:p-6 space-y-4">
            <a
              href={BUSINESS_INFO.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[#dabeb9] py-3.5 text-center text-sm font-bold text-black shadow-xs hover:bg-[#c9a7a0]"
            >
              <i className="fa-solid fa-calendar-check text-black" aria-hidden="true" />
              <span>Book tid online</span>
            </a>

            <div className="pt-2 text-center text-xs text-black space-y-1">
              <p className="font-semibold text-black">{BUSINESS_INFO.name}</p>
              <p>{BUSINESS_INFO.address}</p>
              <p className="pt-1">
                <a
                  href={`tel:${BUSINESS_INFO.phoneRaw}`}
                  className="font-bold text-black hover:underline"
                >
                  {BUSINESS_INFO.phone}
                </a>
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
